import { useState } from 'react'
import { useSelector } from 'react-redux'
import { Mail } from 'lucide-react'
import type { RootState } from '../store'
import type { Project } from '../data/projectTypes'
import { isDevMock } from '../dev/isDevMock'

interface CompletionEmailConfigProps {
  project: Project
}

export default function CompletionEmailConfig({ project }: CompletionEmailConfigProps) {
  const userEmail = useSelector((state: RootState) => state.auth.user?.email ?? null)
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null)

  async function handleSend() {
    setSending(true)
    setResult(null)
    try {
      if (isDevMock()) {
        setResult({ ok: true, message: `Completion email sent to ${userEmail ?? 'your account'} (mock — no email sent).` })
        setSending(false)
        return
      }
      const res = await fetch(`/api/projects/${project.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'send-completion-email' }),
      })
      const data = await res.json()
      if (data.success) {
        const to = data.email ?? userEmail ?? 'your account'
        setResult({
          ok: true,
          message: data.queued
            ? `QA is still running. We'll email the results to ${to} as soon as it finishes.`
            : `Completion email sent to ${to}.`,
        })
      } else {
        setResult({ ok: false, message: data.error ?? `Failed to send (status ${res.status}).` })
      }
    } catch {
      setResult({ ok: false, message: 'Could not reach the server.' })
    }
    setSending(false)
  }

  return (
    <section className="settings-section" data-testid="completion-email-config">
      <h3 className="settings-section__title">
        <Mail size={16} aria-hidden="true" /> Completion Email
      </h3>
      <p className="settings-section__desc">
        Email a summary of this project's QA results{userEmail ? <> to <strong>{userEmail}</strong></> : ''}. The
        email lists the overall results with a link to the overview and a short description and link for each bug
        found, so you can copy individual bug reports — or every bug report — from the UI and hand them to your agent.
        If QA is still running, the email is sent automatically once it finishes; otherwise it is sent right away.
      </p>
      {result && (
        <p
          className={`settings-section__test-result ${result.ok ? 'settings-section__test-result--success' : 'settings-section__test-result--error'}`}
          role={result.ok ? undefined : 'alert'}
          data-testid="completion-email-result"
        >
          {result.message}
        </p>
      )}
      <button
        type="button"
        className="settings-section__save-btn"
        onClick={handleSend}
        disabled={sending}
        data-testid="send-completion-email-btn"
      >
        <Mail size={14} aria-hidden="true" /> {sending ? 'Sending...' : 'Send Completion Email'}
      </button>
    </section>
  )
}
